/**
 * Native dashboards — warehouse-backed widgets over the semantic layer.
 * KPI cards with deltas, SVG timeseries/breakdowns, add-widget modal,
 * share links. The Executive Overview auto-provisions on first load.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineGrid,
  InlineStack,
  Modal,
  Page,
  Select,
  SkeletonBodyText,
  SkeletonPage,
  Text,
  TextField,
} from '@shopify/polaris';

import {
  addWidget,
  createDashboard,
  createShareToken,
  deleteDashboard,
  deleteWidget,
  getDashboardData,
  listAskMetrics,
  listDashboards,
  revokeShareToken,
} from '../services/marketingSuiteApi';
import type {
  Dashboard,
  DashboardData,
  MetricInfo,
  MetricResult,
  WidgetData,
} from '../types/marketingSuite';
import {
  BarChart,
  KpiValue,
  LineChart,
} from '../components/marketingSuite/MiniChart';
import { MetricTable } from '../components/marketingSuite/MetricTable';

function isResult(data: WidgetData | undefined): data is MetricResult {
  return !!data && !('error' in data);
}

export default function Dashboards() {
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [metrics, setMetrics] = useState<MetricInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Keyed to the dashboard it was minted for: a bare token survived
  // switching dashboards, so A's capability URL kept displaying under
  // B and could be distributed as the wrong dashboard (PR #11 review).
  const [share, setShare] = useState<{
    dashboardId: string;
    token: string;
  } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [newMetric, setNewMetric] = useState('spend');
  const [newType, setNewType] = useState('kpi');
  const [newBreakdown, setNewBreakdown] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');

  const active = useMemo(
    () => dashboards.find((d) => d.id === activeId) ?? null,
    [dashboards, activeId]
  );

  const load = useCallback(async () => {
    try {
      // allSettled: a metrics-picker failure must not blank the dashboards
      // that loaded fine — it only degrades the add-widget modal.
      const [listResult, metricResult] = await Promise.allSettled([
        listDashboards(),
        listAskMetrics(),
      ]);
      if (listResult.status === 'rejected') throw listResult.reason;
      const list = listResult.value;
      setDashboards(list);
      if (metricResult.status === 'fulfilled') {
        setMetrics(metricResult.value.metrics);
        setMetricsError(null);
      } else {
        // Without the registry the modal would silently offer an empty
        // Metric picker and still submit the default (PR #11 review).
        setMetricsError(
          'Metric list unavailable — widgets cannot be added right now.'
        );
      }
      const first = list.find((d) => d.is_default) ?? list[0];
      setActiveId((prev) => prev ?? first?.id ?? null);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboards');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    setData(null);
    getDashboardData(activeId)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : 'Failed to resolve widgets'
          );
        }
      });
    return () => {
      cancelled = true; // a superseded request must not paint stale widgets
    };
  }, [activeId, dashboards]);

  const onCreate = async () => {
    const name = newDashboardName.trim();
    if (!name) return;
    try {
      const created = await createDashboard(name);
      setShowCreate(false);
      setNewDashboardName('');
      await load();
      setActiveId(created.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    }
  };

  const onDeleteWidget = (dashboardId: string, widgetId: string) => {
    void deleteWidget(dashboardId, widgetId)
      .then(load)
      .catch((e) =>
        setError(e instanceof Error ? e.message : 'Delete widget failed')
      );
  };

  const onShare = async () => {
    if (!active) return;
    try {
      const { share_token } = await createShareToken(active.id);
      setShare({ dashboardId: active.id, token: share_token });
      setNotice(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Share failed');
    }
  };

  // A distributed capability URL must be revocable from the UI — the
  // backend's DELETE /share existed with no control invoking it, so a
  // leaked link could only be invalidated by a manual API request
  // (PR #11 review).
  const onRevoke = async () => {
    if (!active) return;
    try {
      await revokeShareToken(active.id);
      setShare(null);
      setNotice(
        'Share link revoked — previously distributed URLs no longer work.'
      );
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Revoke failed');
    }
  };

  const onAddWidget = async () => {
    if (!active) return;
    try {
      const metricInfo = metrics.find((m) => m.key === newMetric);
      await addWidget(active.id, {
        widget_type: newType,
        title:
          newTitle ||
          `${metricInfo?.label ?? newMetric}${newType === 'breakdown' && newBreakdown ? ` by ${newBreakdown}` : ''}`,
        metric_query: {
          metric: newMetric,
          period_type: metricInfo?.period_types.includes('weekly')
            ? 'weekly'
            : metricInfo?.period_types[0],
          window: newType === 'breakdown' ? 1 : 12,
          ...(newType === 'breakdown' && newBreakdown
            ? { breakdown: newBreakdown }
            : {}),
        },
      });
      setShowAdd(false);
      setNewTitle('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Add widget failed');
    }
  };

  if (loading) {
    return (
      <SkeletonPage title="Dashboards">
        <Card>
          <SkeletonBodyText lines={8} />
        </Card>
      </SkeletonPage>
    );
  }

  const metricOptions = metrics.map((m) => ({
    label: m.label,
    value: m.key,
  }));
  const selectedMetric = metrics.find((m) => m.key === newMetric);

  return (
    <Page
      title="Dashboards"
      subtitle="Your numbers, refreshed from your connected sources each time you open this page."
      primaryAction={{ content: 'Add widget', onAction: () => setShowAdd(true), disabled: !active }}
      secondaryActions={[
        { content: 'New dashboard', onAction: () => setShowCreate(true) },
        { content: 'Share link', onAction: () => void onShare(), disabled: !active },
        {
          content: 'Revoke share link',
          onAction: () => void onRevoke(),
          disabled:
            !active ||
            (!active.has_share_token && share?.dashboardId !== active.id),
        },
        {
          content: 'Delete dashboard',
          destructive: true,
          onAction: () => {
            if (active && window.confirm(`Delete “${active.name}”?`)) {
              void deleteDashboard(active.id)
                .then(() => {
                  setActiveId(null);
                  return load();
                })
                .catch((e) =>
                  setError(
                    e instanceof Error ? e.message : 'Delete failed'
                  )
                );
            }
          },
          disabled: !active || active.is_default,
        },
      ]}
    >
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
        {notice && (
          <Banner tone="success" onDismiss={() => setNotice(null)}>
            {notice}
          </Banner>
        )}
        {share && share.dashboardId === activeId && (
          <Banner
            title="Read-only share link created"
            tone="success"
            onDismiss={() => setShare(null)}
          >
            <Text as="p" breakWord>
              {`${window.location.origin}/share?token=${share.token}`}
            </Text>
          </Banner>
        )}
        <InlineStack gap="200">
          {dashboards.map((d) => (
            <Button
              key={d.id}
              variant={d.id === activeId ? 'primary' : 'tertiary'}
              size="slim"
              onClick={() => setActiveId(d.id)}
            >
              {d.name}
            </Button>
          ))}
        </InlineStack>
        {active && (
          <>
            <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="300">
              {active.widgets
                .filter((w) => w.widget_type === 'kpi')
                .map((w) => {
                  const wd = data?.widgets[w.id];
                  return (
                    <Card key={w.id}>
                      <BlockStack gap="100">
                        <InlineStack align="space-between">
                          <Text as="h3" variant="headingSm" tone="subdued">
                            {w.title}
                          </Text>
                          <Button
                            variant="plain"
                            size="micro"
                            accessibilityLabel={`Delete ${w.title}`}
                            onClick={() => onDeleteWidget(active.id, w.id)}
                          >
                            ✕
                          </Button>
                        </InlineStack>
                        {isResult(wd) ? (
                          <KpiValue
                            rows={wd.rows}
                            unit={wd.unit}
                            directionIsImprovement={
                              wd.direction_is_improvement
                            }
                          />
                        ) : wd ? (
                          <Badge tone="attention">
                            {('message' in wd && wd.message) || wd.error}
                          </Badge>
                        ) : (
                          <SkeletonBodyText lines={1} />
                        )}
                      </BlockStack>
                    </Card>
                  );
                })}
            </InlineGrid>
            <InlineGrid columns={{ xs: 1, lg: 2 }} gap="300">
              {active.widgets
                .filter((w) => w.widget_type !== 'kpi')
                .map((w) => {
                  const wd = data?.widgets[w.id];
                  return (
                    <Card key={w.id}>
                      <BlockStack gap="200">
                        <InlineStack align="space-between">
                          <Text as="h3" variant="headingSm">
                            {w.title}
                          </Text>
                          <Button
                            variant="plain"
                            size="micro"
                            accessibilityLabel={`Delete ${w.title}`}
                            onClick={() => onDeleteWidget(active.id, w.id)}
                          >
                            ✕
                          </Button>
                        </InlineStack>
                        {isResult(wd) ? (
                          w.widget_type === 'breakdown' ? (
                            <BarChart rows={wd.rows} unit={wd.unit} />
                          ) : w.widget_type === 'table' ? (
                            <MetricTable rows={wd.rows} unit={wd.unit} />
                          ) : (
                            <LineChart rows={wd.rows} unit={wd.unit} />
                          )
                        ) : wd ? (
                          <Banner tone="warning">
                            {('message' in wd && wd.message) ||
                              'No data for this widget yet.'}
                          </Banner>
                        ) : (
                          <SkeletonBodyText lines={4} />
                        )}
                      </BlockStack>
                    </Card>
                  );
                })}
            </InlineGrid>
          </>
        )}
      </BlockStack>

      <Modal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        title="Add widget"
        primaryAction={{
          content: 'Add',
          // A breakdown without a dimension resolves to one ungrouped
          // row; no metric registry means nothing valid to submit.
          disabled:
            metrics.length === 0 ||
            (newType === 'breakdown' && !newBreakdown),
          onAction: () => void onAddWidget(),
        }}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {metricsError && <Banner tone="warning">{metricsError}</Banner>}
            <Select
              label="Metric"
              options={metricOptions}
              value={newMetric}
              onChange={(value) => {
                setNewMetric(value);
                // A stale dimension from the previous metric would submit
                // a query the backend 422s (PR #11 review).
                const dims =
                  metrics.find((m) => m.key === value)?.dimensions ?? [];
                if (newBreakdown && !dims.includes(newBreakdown)) {
                  setNewBreakdown('');
                }
              }}
              helpText={selectedMetric?.description}
            />
            <Select
              label="Widget type"
              options={[
                { label: 'KPI card', value: 'kpi' },
                { label: 'Timeseries', value: 'timeseries' },
                { label: 'Breakdown', value: 'breakdown' },
                { label: 'Table', value: 'table' },
              ]}
              value={newType}
              onChange={setNewType}
            />
            {newType === 'breakdown' && (
              <Select
                label="Break down by"
                options={(selectedMetric?.dimensions ?? []).map((d) => ({
                  label: d,
                  value: d,
                }))}
                value={newBreakdown}
                onChange={setNewBreakdown}
                placeholder="Choose a dimension"
              />
            )}
            <TextField
              label="Title (optional)"
              value={newTitle}
              onChange={setNewTitle}
              autoComplete="off"
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New dashboard"
        primaryAction={{
          content: 'Create',
          disabled: !newDashboardName.trim(),
          onAction: () => void onCreate(),
        }}
      >
        <Modal.Section>
          <TextField
            label="Name"
            value={newDashboardName}
            onChange={setNewDashboardName}
            autoComplete="off"
            maxLength={255}
          />
        </Modal.Section>
      </Modal>
    </Page>
  );
}
